import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ChevronDown } from 'lucide-react';
import { 
  MessageSquare, 
  TrendingUp, 
  TrendingDown, 
  Star,
  Filter,
  Eye,
  Phone,
  Volume2,
  Info,
  Tag,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import QualifierPill from '@/components/QualifierPill';
import { format } from 'date-fns';
import { safeHtml } from '@/lib/sanitize';
import { cleanQuestionLabel } from '@/lib/answerFormatting';
import { NpsFace } from '@/components/NpsFace';

const _RemovedLocalNpsFace = ({ score, size = 32 }) => {
  if (score == null) return null;
  // 0-6 sad (red), 7-8 neutral (amber), 9-10 happy (green)
  const n = Number(score);
  const color = n <= 6 ? '#E74C3C' : n <= 8 ? '#F39C12' : '#27AE60';
  const mood = n <= 6 ? 'sad' : n <= 8 ? 'neutral' : 'happy';
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className="shrink-0"
      data-testid={`nps-face-${n}`}
      aria-label={`NPS® ${n} ${mood}`}
      role="img"
    >
      <circle cx="32" cy="32" r="30" fill={color} />
      {/* Eyes */}
      <circle cx="22" cy="26" r="3" fill="#1E293B" />
      <circle cx="42" cy="26" r="3" fill="#1E293B" />
      {/* Mouth */}
      {n <= 6 && (
        <path
          d="M20 46 Q32 34 44 46"
          stroke="#1E293B"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
      )}
      {(n === 7 || n === 8) && (
        <line
          x1="22"
          y1="44"
          x2="42"
          y2="44"
          stroke="#1E293B"
          strokeWidth="3"
          strokeLinecap="round"
        />
      )}
      {n >= 9 && (
        <path
          d="M20 40 Q32 52 44 40"
          stroke="#1E293B"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
};
void _RemovedLocalNpsFace;

// Resolve the per-response NPS presentation snapshot (captured at submission).
// A response has a single tap-NPS value, so take the first snapshot entry.
// Legacy responses (no snapshot) keep the previous colour-face presentation.
const npsPresFor = (resp) => {
  const pres = Object.values((resp && resp.nps_presentation) || {})[0];
  return { showFace: !pres || pres.style !== 'numbers', appearance: (pres && pres.appearance) || 'color' };
};

function MatrixAggregation({ matrix, qid }) {
  const [openItems, setOpenItems] = useState({});
  return (
    <div className="mt-2 space-y-2" data-testid={`qa-matrix-${qid}`}>
      {matrix.map((crit, i) => {
        const isOpen = !!openItems[crit.criterion];
        const total = (crit.distribution || []).reduce((s, d) => s + (d.count || 0), 0);
        return (
          <div key={i} className="border border-[#F1F5F9] rounded p-2 bg-white">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <p className="text-[11px] font-semibold text-[#0F172A]">{crit.criterion}</p>
              <span className="text-[10px] text-[#94A3B8]">{total} ratings</span>
            </div>
            <div className="space-y-1">
              {(crit.distribution || []).map((d, di) => (
                <div key={di} className="flex items-center gap-2 text-[11px]">
                  <span className="w-28 truncate text-[#334155]">{d.label}</span>
                  <div className="flex-1 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                    <div className="h-full bg-[#0EA5E9]" style={{ width: `${d.pct}%` }} />
                  </div>
                  <span className="w-14 text-right text-[#64748B]">{d.count} &middot; {d.pct}%</span>
                </div>
              ))}
            </div>
            {Array.isArray(crit.items) && crit.items.length > 0 && (
              <button
                type="button"
                onClick={() => setOpenItems(o => ({ ...o, [crit.criterion]: !o[crit.criterion] }))}
                className="text-[10px] text-[#1A3E6F] hover:underline font-medium mt-1.5"
                data-testid={`qa-matrix-toggle-${qid}-${i}`}
              >
                {isOpen ? 'Hide item breakdown' : `Show item breakdown (${crit.items.length} items)`}
              </button>
            )}
            {isOpen && Array.isArray(crit.items) && (
              <div className="mt-1.5 space-y-1.5 border-t border-[#F1F5F9] pt-1.5">
                {crit.items.map((it, ii) => (
                  <div key={ii} className="text-[10px]">
                    <p className="font-medium text-[#0F172A] mb-0.5">{it.item}</p>
                    <div className="flex flex-wrap gap-1">
                      {(it.distribution || []).map((d, di) => (
                        <span key={di} className="px-1.5 py-0.5 rounded bg-[#F8FAFC] border border-[#F1F5F9] text-[#334155]">
                          {d.label}: <span className="font-semibold">{d.count}</span> ({d.pct}%)
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}



const QuestionAggregationsCard = ({ aggregations, surveyQualifiersById = {} }) => {
  // Local expanded-state map (question_id -> boolean) for text sample expand/collapse.
  const [expanded, setExpanded] = useState({});
  if (!aggregations || aggregations.length === 0) return null;
  const toggle = (qid) => setExpanded(prev => ({ ...prev, [qid]: !prev[qid] }));
  const TEXT_TYPES = new Set(['short_text', 'long_text', 'text', 'voice']);
  const DEFAULT_VISIBLE = 5;
  return (
    <Card className="border-[#E2E8F0] shadow-sm" data-testid="question-aggregations-card">
      <CardContent className="space-y-3 pt-4">
        {aggregations.map((q) => {
          const isText = TEXT_TYPES.has(q.type);
          const samples = Array.isArray(q.recent_samples) ? q.recent_samples : [];
          const isExpanded = !!expanded[q.question_id];
          const visibleSamples = isText && !isExpanded ? samples.slice(0, DEFAULT_VISIBLE) : samples;
          return (
          <div key={q.question_id} className="border border-[#F1F5F9] rounded-lg p-3" data-testid={`qa-${q.question_id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-xs font-semibold text-[#0F172A]" dangerouslySetInnerHTML={safeHtml(q.text || q.type)} />
                {/* Part B3: TrustX® dimension pill */}
                {q.qualifier_id && (
                  <QualifierPill
                    qualifierId={q.qualifier_id}
                    qualifiers={(surveyQualifiersById[q.survey_id]) || []}
                  />
                )}
              </div>
              <span className="text-[10px] px-1.5 py-0.5 bg-[#F1F5F9] rounded text-[#64748B] shrink-0">{q.response_count} resp</span>
            </div>
            {/* NPS: render NPS score + promoter/passive/detractor breakdown only.
                Non-NPS rating questions keep the existing Avg score display. */}
            {q.type === 'nps' ? (
              q.nps_score != null && (
                <div className="space-y-1.5" data-testid={`qa-nps-${q.question_id}`}>
                  <p className="text-xs text-[#334155]">Net Promoter&reg; Score: <span className="font-semibold">{Math.round(Number(q.nps_score))}</span></p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#334155]">
                    <span><span className="inline-block w-2 h-2 rounded-full bg-[#4EBC87] mr-1" />Promoters (9-10): <span className="font-semibold">{Number(q.promoters_pct ?? 0).toFixed(1)}%</span> ({q.promoters ?? 0})</span>
                    <span><span className="inline-block w-2 h-2 rounded-full bg-[#F59E0B] mr-1" />Passives (7-8): <span className="font-semibold">{Number(q.passives_pct ?? 0).toFixed(1)}%</span> ({q.passives ?? 0})</span>
                    <span><span className="inline-block w-2 h-2 rounded-full bg-[#D62B27] mr-1" />Detractors (0-6): <span className="font-semibold">{Number(q.detractors_pct ?? 0).toFixed(1)}%</span> ({q.detractors ?? 0})</span>
                  </div>
                </div>
              )
            ) : (
              q.average_score != null && (
                <p className="text-xs text-[#334155]">Avg score: <span className="font-semibold">{Number(q.average_score).toFixed(2)}</span></p>
              )
            )}
            {Array.isArray(q.distribution) && q.distribution.length > 0 && (
              <div className="space-y-1 mt-1.5">
                {q.distribution.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span className="w-28 truncate text-[#334155]">{d.label}</span>
                    <div className="flex-1 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                      <div className="h-full bg-[#1A3E6F]" style={{ width: `${d.pct}%` }} />
                    </div>
                    <span className="w-14 text-right text-[#64748B]">{d.count} &middot; {d.pct}%</span>
                  </div>
                ))}
              </div>
            )}
            {visibleSamples.length > 0 && (
              <div className="mt-1.5 space-y-1" data-testid={`qa-samples-${q.question_id}`}>
                {visibleSamples.map((s, i) => (
                  <p key={i} className="text-[11px] text-[#64748B] italic bg-[#F8FAFC] rounded px-2 py-1 border border-[#F1F5F9] whitespace-pre-wrap break-words">&ldquo;{s}&rdquo;</p>
                ))}
                {isText && samples.length > DEFAULT_VISIBLE && (
                  <button
                    type="button"
                    onClick={() => toggle(q.question_id)}
                    className="text-[11px] text-[#1A3E6F] hover:underline font-medium mt-1"
                    data-testid={`qa-samples-toggle-${q.question_id}`}
                  >
                    {isExpanded ? 'Show less' : `Show all ${samples.length} responses`}
                  </button>
                )}
              </div>
            )}
            {q.type === 'matrix' && Array.isArray(q.matrix) && q.matrix.length > 0 && (
              <MatrixAggregation matrix={q.matrix} qid={q.question_id} />
            )}
          </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const CsatResultsCard = ({ csatResults }) => {
  const perQuestion = (csatResults || []).filter(r => r.question_id !== '__survey__');
  if (perQuestion.length === 0) return null;
  return (
    <Card className="border-[#E2E8F0] shadow-sm" data-testid="csat-results-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#0F172A] font-['Montserrat']">CSAT Results</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {perQuestion.map((r) => (
          <div key={`${r.survey_id}-${r.question_id}-${r.period}-${r.branch_id}`} className="flex items-center justify-between gap-3 border border-[#F1F5F9] rounded-lg px-3 py-2" data-testid={`csat-row-${r.question_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[#0F172A] truncate">{cleanQuestionLabel(r.question_text) || 'CSAT Question'}</p>
              <p className="text-[10px] text-[#94A3B8]">
                {r.period} &middot; rule: {r.top_box_rule === 'top_2' ? 'Top 2 (4-5)' : 'Top 1 (5 only)'} &middot; {r.total_responses} responses
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-lg font-bold text-[#1A3E6F]">{Number(r.csat_percentage || 0).toFixed(1)}%</p>
              <p className="text-[10px] text-[#64748B]">Avg: {Number(r.average_score || 0).toFixed(2)}</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const NpsBreakdownBar = ({ npsBreakdown, total, activeCategory, onSegmentClick }) => {
  if (!npsBreakdown || total === 0) return null;
  const { promoters = 0, passives = 0, detractors = 0 } = npsBreakdown;
  const pPct = Math.round((promoters / total) * 100);
  const psPct = Math.round((passives / total) * 100);
  const dPct = Math.round((detractors / total) * 100);
  const npsScore = npsBreakdown.nps_score != null ? Number(npsBreakdown.nps_score) : (pPct - dPct);

  const Seg = ({ count, pct, color, category, label }) => {
    if (count === 0) return null;
    const isActive = activeCategory === category;
    const clickable = typeof onSegmentClick === 'function';
    return (
      <button
        type="button"
        onClick={clickable ? () => onSegmentClick(category) : undefined}
        disabled={!clickable}
        style={{ width: `${pct}%`, backgroundColor: color }}
        className={`flex items-center justify-center transition-all duration-200
          ${clickable ? 'hover:opacity-80 cursor-pointer' : ''}
          ${isActive ? 'ring-2 ring-offset-1 ring-[#0F172A] brightness-110' : ''}
        `}
        data-testid={`nps-bar-segment-${category}`}
        aria-pressed={isActive}
        aria-label={`${label} ${pct}% (${count})`}
        title={isActive ? `${label} (active — click to clear)` : `${label}: ${count} (${pct}%)`}
      >
        {pct >= 12 && <span className="text-[10px] font-semibold text-white">{pct}%</span>}
      </button>
    );
  };

  return (
    <Card className="border-[#E2E8F0] shadow-sm" data-testid="nps-breakdown-bar">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-[#0F172A] font-['Montserrat']">Net Promoter&reg; Score Breakdown</p>
          <span className={`text-sm font-bold font-['Montserrat'] ${npsScore > 0 ? 'text-[#4EBC87]' : npsScore < 0 ? 'text-[#D62B27]' : 'text-[#64748B]'}`}>
            {npsScore > 0 ? '+' : ''}{Math.round(Number(npsScore))}
          </span>
        </div>
        <div className="flex w-full h-6 rounded-full overflow-hidden bg-[#F1F5F9]">
          <Seg count={promoters} pct={pPct} color="#4EBC87" category="promoter" label="Promoters" />
          <Seg count={passives} pct={psPct} color="#F59E0B" category="passive" label="Passives" />
          <Seg count={detractors} pct={dPct} color="#D62B27" category="detractor" label="Detractors" />
        </div>
        <div className="flex justify-between mt-2 text-[11px] text-[#64748B]">
          <span><span className="inline-block w-2 h-2 rounded-full bg-[#4EBC87] mr-1" />Promoters {pPct}% ({promoters})</span>
          <span><span className="inline-block w-2 h-2 rounded-full bg-[#F59E0B] mr-1" />Passives {psPct}% ({passives})</span>
          <span><span className="inline-block w-2 h-2 rounded-full bg-[#D62B27] mr-1" />Detractors {dPct}% ({detractors})</span>
        </div>
      </CardContent>
    </Card>
  );
};

const NpsInfoTooltip = () => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="p-1 rounded-full hover:bg-[#F1F5F9] transition-colors"
        data-testid="nps-info-btn"
        aria-label="How NPS® is calculated"
      >
        <Info className="w-4 h-4 text-[#94A3B8]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-50 w-80 bg-white rounded-xl shadow-xl border border-[#E2E8F0] p-4" data-testid="nps-info-panel">
            <p className="text-sm font-semibold text-[#0F172A] mb-2 font-['Montserrat']">How NPS® is calculated</p>
            <p className="text-xs text-[#64748B] mb-3 leading-relaxed">
              NPS® is calculated by subtracting the percentage of Detractors from the percentage of Promoters. Passives are not counted in the formula.
            </p>
            <div className="space-y-2 mb-3">
              <div className="flex items-start gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[#4EBC87] mt-1.5 shrink-0" />
                <p className="text-xs text-[#334155]"><span className="font-semibold">Promoters (9-10)</span> — Loyal customers who are likely to recommend you.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[#F59E0B] mt-1.5 shrink-0" />
                <p className="text-xs text-[#334155]"><span className="font-semibold">Passives (7-8)</span> — Satisfied but not enthusiastic. They do not affect your NPS® score.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[#D62B27] mt-1.5 shrink-0" />
                <p className="text-xs text-[#334155]"><span className="font-semibold">Detractors (0-6)</span> — Unhappy customers who may discourage others.</p>
              </div>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-2.5 mb-3">
              <p className="text-xs text-[#334155]"><span className="font-semibold">Formula:</span> NPS® = % Promoters - % Detractors</p>
              <p className="text-xs text-[#64748B] mt-1">Example: If 60% are Promoters and 15% are Detractors, your NPS® is 45.</p>
            </div>
            <p className="text-xs text-[#64748B] mb-2">NPS® ranges from -100 to +100. Above 0 means more Promoters than Detractors. Above 50 is excellent.</p>
            <p className="text-[9px] text-[#CBD5E1] leading-relaxed">Net Promoter®, NPS® are registered trademarks of Bain &amp; Company Inc., NICE Systems Inc., and Fred Reichheld. Net Promoter® Score and Net Promoter® System are service marks of Bain &amp; Company Inc., NICE Systems Inc., and Fred Reichheld.</p>
          </div>
        </>
      )}
    </div>
  );
};

export default function SurveyResponses() {
  const { user, canExport } = useAuth();
  const navigate = useNavigate();
  // Read URL params (?sentiment=…, ?focus=nps, ?nps_category=…) so dashboard cards can deep-link.
  const initialSentiment = (() => {
    if (typeof window === 'undefined') return 'all';
    const sp = new URLSearchParams(window.location.search);
    const s = (sp.get('sentiment') || '').toLowerCase();
    return ['positive', 'neutral', 'negative'].includes(s) ? s : 'all';
  })();
  const initialNpsCategory = (() => {
    if (typeof window === 'undefined') return 'all';
    const sp = new URLSearchParams(window.location.search);
    const c = (sp.get('nps_category') || '').toLowerCase();
    return ['promoter', 'passive', 'detractor'].includes(c) ? c : 'all';
  })();
  const focusParam = (typeof window !== 'undefined')
    ? new URLSearchParams(window.location.search).get('focus')
    : null;
  const initialTab = (() => {
    if (typeof window === 'undefined') return 'responses';
    const sp = new URLSearchParams(window.location.search);
    const t = (sp.get('tab') || '').toLowerCase();
    return t === 'summary' ? 'summary' : 'responses';
  })();
  const [responses, setResponses] = useState([]);
  const [stats, setStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  // Multi-select branches: empty array = "All branches".
  const [filterBranches, setFilterBranches] = useState([]);
  const [filterSentiment, setFilterSentiment] = useState(initialSentiment);
  const [filterNpsCategory, setFilterNpsCategory] = useState(initialNpsCategory);
  const [branchPickerOpen, setBranchPickerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Persist the active tab into the URL (?tab=responses|summary) so the
  // browser back button and shared links work as expected. Strips the
  // param when on the default tab to keep URLs clean.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    if (activeTab === 'summary') sp.set('tab', 'summary');
    else sp.delete('tab');
    const next = sp.toString();
    const newUrl = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
  }, [activeTab]);
  // Export CSV modal state
  const [exportOpen, setExportOpen] = useState(false);
  const [exportSurveys, setExportSurveys] = useState([]);
  const [exportSurveyId, setExportSurveyId] = useState('');
  const [exportBranch, setExportBranch] = useState('all');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    fetchBranches();
    fetchStats();
    if (canExport) fetchExportSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranches, filterSentiment]);

  // Scroll to the Net Promoter® Score Breakdown bar at the top of the page
  // when arriving with ?focus=nps so the dashboard's "View details →" KPI
  // card lands the user directly on the breakdown bar.
  useEffect(() => {
    if (!focusParam || focusParam !== 'nps' || !stats) return;
    const t = setTimeout(() => {
      const target = document.querySelector('[data-testid="nps-breakdown-bar"]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(t);
  }, [focusParam, stats]);

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API}/branches`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Pull from /dashboard/analytics which has nps_breakdown + question_aggregations + csat_results
      const response = await fetch(`${API}/dashboard/analytics`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const merged = {
          ...(data.response_stats || {}),
          question_aggregations: data.question_aggregations || [],
          csat_results: data.csat_results || [],
          // Issue 1: forward the full question lookup so detailNumberMap can
          // compute hierarchical numbers (1.1, 1.2, ...) in Response Details.
          // Without this, the filter on survey_id returns an empty list and
          // no numbers render.
          survey_questions: data.survey_questions || [],
        };
        setStats(merged);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchResponses = async () => {
    try {
      let url = `${API}/survey/responses`;
      const params = new URLSearchParams();
      // Use server-side branch filter only when exactly one branch is selected;
      // multi-branch filtering is applied client-side after fetch.
      if (filterBranches.length === 1) params.append('branch_id', filterBranches[0]);
      if (filterSentiment && filterSentiment !== 'all') params.append('sentiment', filterSentiment);
      // Pull a larger window so the local NPS breakdown matches the dashboard
      // aggregation when filters cap the visible set. The default backend
      // limit is 100, which made the breakdown bar diverge from the
      // dashboard for tenants with > 100 responses.
      params.append('limit', '2000');
      url += `?${params.toString()}`;

      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setResponses(data);
      }
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast.error('Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  const fetchExportSurveys = async () => {
    try {
      const res = await fetch(`${API}/surveys/builder`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data?.surveys || []);
      setExportSurveys(list);
      // Default to first active survey, fall back to first survey overall.
      const active = list.find(s => s.status === 'active');
      const pick = active || list[0];
      if (pick) setExportSurveyId(pick.survey_id || pick.id);
    } catch (error) {
      console.error('Error fetching surveys for export:', error);
    }
  };

  const openExport = () => {
    if (exportSurveys.length === 0) fetchExportSurveys();
    setExportOpen(true);
  };

  const runExport = async () => {
    if (!exportSurveyId) {
      toast.error('Select a survey to export');
      return;
    }
    setExporting(true);
    try {
      const params = new URLSearchParams({ survey_id: exportSurveyId });
      if (exportBranch && exportBranch !== 'all') params.append('branch_id', exportBranch);
      if (exportFrom) params.append('date_from', exportFrom);
      if (exportTo) params.append('date_to', exportTo);
      const res = await fetch(`${API}/responses/export?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to export responses');
        return;
      }
      const blob = await res.blob();
      let filename = 'responses.csv';
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
      setExportOpen(false);
    } catch {
      toast.error('Failed to export responses');
    } finally {
      setExporting(false);
    }
  };

  const getSentimentBadge = (sentiment) => {
    const styles = {
      positive: 'bg-[#4EBC87]/15 text-[#059669]',
      neutral: 'bg-[#F1D511]/20 text-[#b45309]',
      negative: 'bg-[#D62B27]/15 text-[#D62B27]'
    };
    return styles[sentiment] || styles.neutral;
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const hasVoice = (response) => !!(response.nps_reason_voice_url || response.suggestions_voice_url);

  // Apply client-side filters that the server doesn't handle: multi-branch
  // (when 2+ branches chosen) and NPS category bucketing.
  const filteredResponses = useMemo(() => {
    let list = responses;
    if (filterBranches.length > 1) {
      const set = new Set(filterBranches);
      list = list.filter(r => set.has(r.branch_id));
    }
    if (filterNpsCategory !== 'all') {
      list = list.filter(r => {
        const n = Number(r.nps_score);
        if (Number.isNaN(n)) return false;
        if (filterNpsCategory === 'promoter') return n >= 9;
        if (filterNpsCategory === 'passive') return n >= 7 && n <= 8;
        if (filterNpsCategory === 'detractor') return n >= 0 && n <= 6;
        return true;
      });
    }
    return list;
  }, [responses, filterBranches, filterNpsCategory]);

  // Recompute the NPS breakdown bar so it stays consistent with the
  // Dashboard's aggregation. When no filters that change the underlying set
  // are active (no branch/sentiment filter), we use `stats.nps_breakdown`
  // which is computed by the same MongoDB aggregation the dashboard uses —
  // guaranteeing identical totals between the two pages. When the user
  // applies branch/sentiment filters, we recompute over the fetched list
  // (raised to limit=2000 above so it's effectively the full set).
  const filteredNpsBreakdown = useMemo(() => {
    const useGlobal = filterBranches.length === 0 && filterSentiment === 'all';
    let promoters = 0, passives = 0, detractors = 0, total = 0, npsScore = 0;
    if (useGlobal && stats?.nps_breakdown) {
      promoters = Number(stats.nps_breakdown.promoters || 0);
      passives = Number(stats.nps_breakdown.passives || 0);
      detractors = Number(stats.nps_breakdown.detractors || 0);
      total = promoters + passives + detractors;
      npsScore = stats.nps_breakdown.nps_score != null
        ? Math.round(Number(stats.nps_breakdown.nps_score))
        : (total > 0 ? Math.round((promoters - detractors) / total * 100) : 0);
    } else {
      // Compute from server-fetched responses already narrowed by single
      // branch / sentiment via the API. For multi-branch we still need to
      // apply the client-side branch narrowing here.
      const set = filterBranches.length > 1 ? new Set(filterBranches) : null;
      responses.forEach(r => {
        if (set && !set.has(r.branch_id)) return;
        const n = Number(r.nps_score);
        if (Number.isNaN(n)) return;
        total += 1;
        if (n >= 9) promoters += 1;
        else if (n >= 7) passives += 1;
        else if (n >= 0) detractors += 1;
      });
      npsScore = total > 0 ? Math.round((promoters - detractors) / total * 100) : 0;
    }
    // NPS category filter narrows the bar's visible counts (per spec: "bar
    // reflects the filtered set"). The header NPS score keeps the
    // pre-narrowing value so it remains comparable to the dashboard.
    if (filterNpsCategory === 'promoter') { passives = 0; detractors = 0; }
    else if (filterNpsCategory === 'passive') { promoters = 0; detractors = 0; }
    else if (filterNpsCategory === 'detractor') { promoters = 0; passives = 0; }
    const visibleTotal = promoters + passives + detractors;
    return { promoters, passives, detractors, nps_score: npsScore, total: visibleTotal };
  }, [stats, responses, filterBranches, filterSentiment, filterNpsCategory]);

  const handleNpsBarClick = (category) => {
    setFilterNpsCategory(prev => prev === category ? 'all' : category);
  };

  const toggleBranch = (branchId) => {
    setFilterBranches(prev => prev.includes(branchId)
      ? prev.filter(id => id !== branchId)
      : [...prev, branchId]);
  };

  const branchTriggerLabel = (() => {
    if (filterBranches.length === 0) return 'All branches';
    if (filterBranches.length === 1) {
      return branches.find(b => b.branch_id === filterBranches[0])?.name || '1 branch';
    }
    return `${filterBranches.length} branches selected`;
  })();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="responses-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']" data-testid="responses-title">
              Survey Responses
            </h1>
            <p className="text-[#64748B] mt-1">
              View and analyze responses
            </p>
          </div>
          {canExport && (
            <Button
              onClick={openExport}
              variant="outline"
              className="border-[#1A3E6F] text-[#1A3E6F] hover:bg-[#1A3E6F]/5"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          )}
        </div>

        {/* Stats — bar reflects the currently filtered response set */}
        {stats && (
          <NpsBreakdownBar
            npsBreakdown={filteredNpsBreakdown}
            total={filteredNpsBreakdown.total}
            activeCategory={filterNpsCategory === 'all' ? null : filterNpsCategory}
            onSegmentClick={handleNpsBarClick}
          />
        )}

        {/* Avg Ratings — hide when all zero (no legacy data) */}
        {stats && stats.total_responses > 0 && Object.values(stats.avg_ratings || {}).some(v => v && v > 0) && (
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-['Montserrat'] text-[#0F172A]">
                Average Ratings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(stats.avg_ratings).filter(([, v]) => v > 0).map(([key, value]) => (
                  <div key={key} className="text-center p-3 bg-[#F8F9FA] rounded-lg">
                    <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{value}</p>
                    <p className="text-xs text-[#64748B] capitalize">{key.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dynamic per-question aggregations from the tenant's active survey — rendered below the list */}

        {/* CSAT results */}
        {stats && <CsatResultsCard csatResults={stats.csat_results} />}

        {/* Tabs — Responses (default) / Question Summary. Filters live between
            the tab triggers and the tab content so they remain visible and
            apply to both tabs. */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-[#F1F5F9]" data-testid="responses-tabs">
            <TabsTrigger value="responses" data-testid="tab-responses">Responses</TabsTrigger>
            <TabsTrigger value="summary" data-testid="tab-summary">Question Summary</TabsTrigger>
          </TabsList>

          {/* Filters */}
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-2 text-sm text-[#64748B]">
                  <Filter className="w-4 h-4" />
                  <span>Filter:</span>
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                  {/* Multi-select branch filter */}
                  <Popover open={branchPickerOpen} onOpenChange={setBranchPickerOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center justify-between gap-2 w-[200px] h-10 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm text-[#0F172A] hover:border-[#1A3E6F] focus:outline-none focus:ring-1 focus:ring-[#1A3E6F]"
                        data-testid="filter-branches-trigger"
                      >
                        <span className="truncate">{branchTriggerLabel}</span>
                        <ChevronDown className="w-4 h-4 text-[#64748B] shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[240px] p-0" align="start" data-testid="filter-branches-popover">
                      <div className="p-2 border-b border-[#F1F5F9]">
                        <button
                          type="button"
                          onClick={() => setFilterBranches([])}
                          className="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-[#F8FAFC] text-[#0F172A]"
                          data-testid="filter-branches-clear"
                        >
                          <Checkbox checked={filterBranches.length === 0} onCheckedChange={() => setFilterBranches([])} />
                          <span className="font-medium">All branches</span>
                        </button>
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1">
                        {branches.length === 0 ? (
                          <p className="text-xs text-[#94A3B8] px-3 py-2">No branches available</p>
                        ) : branches.map(b => {
                          const checked = filterBranches.includes(b.branch_id);
                          return (
                            <button
                              key={b.branch_id}
                              type="button"
                              onClick={() => toggleBranch(b.branch_id)}
                              className="w-full flex items-center gap-2 text-sm px-3 py-1.5 hover:bg-[#F8FAFC] text-[#0F172A] text-left"
                              data-testid={`filter-branch-option-${b.branch_id}`}
                            >
                              <Checkbox checked={checked} onCheckedChange={() => toggleBranch(b.branch_id)} />
                              <span className="flex-1 truncate">{b.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>

                  <Select value={filterSentiment} onValueChange={setFilterSentiment}>
                    <SelectTrigger className="w-[160px]" data-testid="filter-sentiment">
                      <SelectValue placeholder="All sentiments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sentiments</SelectItem>
                      <SelectItem value="positive">Positive</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                      <SelectItem value="negative">Negative</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filterNpsCategory} onValueChange={setFilterNpsCategory}>
                    <SelectTrigger className="w-[200px]" data-testid="filter-nps-category">
                      <SelectValue placeholder="All NPS® categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="filter-nps-option-all">All NPS® categories</SelectItem>
                      <SelectItem value="promoter" data-testid="filter-nps-option-promoter">Promoters (9-10)</SelectItem>
                      <SelectItem value="passive" data-testid="filter-nps-option-passive">Passives (7-8)</SelectItem>
                      <SelectItem value="detractor" data-testid="filter-nps-option-detractor">Detractors (0-6)</SelectItem>
                    </SelectContent>
                  </Select>

                  {(filterBranches.length > 0 || filterSentiment !== 'all' || filterNpsCategory !== 'all') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setFilterBranches([]); setFilterSentiment('all'); setFilterNpsCategory('all'); }}
                      data-testid="filter-clear-all"
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <TabsContent value="responses" className="space-y-4 mt-0">
            {/* Responses List */}
            <Card className="border-[#E2E8F0] shadow-sm">
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="loading-spinner mx-auto mb-4"></div>
                    <p className="text-[#64748B]">Loading responses...</p>
                  </div>
                ) : filteredResponses.length === 0 ? (
                  <div className="p-12 text-center">
                    <MessageSquare className="w-12 h-12 text-[#B4B4B4] mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-[#0F172A] font-['Montserrat']">No responses yet</h3>
                    <p className="text-[#64748B] mt-1">Customer feedback will appear here</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#E2E8F0]">
                    {filteredResponses.map((response) => (
                  <div 
                    key={response.response_id}
                    className="p-4 hover:bg-[#F8F9FA] transition-colors cursor-pointer"
                    onClick={() => navigate(`/responses/${encodeURIComponent(response.public_id || response.response_id)}`, { state: { fromList: true } })}
                    data-testid={`response-${response.response_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {(response.public_id || response.response_id) && (
                          <p
                            className="text-[10px] font-mono text-[#94A3B8] tracking-wider mb-1.5"
                            data-testid={`resp-public-id-${response.response_id}`}
                          >
                            {response.public_id || response.response_id}
                          </p>
                        )}
                        <div className="flex items-center flex-wrap gap-2 mb-2">
                          {response.nps_score != null && npsPresFor(response).showFace && <NpsFace score={response.nps_score} appearance={npsPresFor(response).appearance} size={32} />}
                          {response.nps_score != null && (() => {
                            const n = Number(response.nps_score);
                            const tone = n >= 9 ? 'bg-[#4EBC87]/15 text-[#4EBC87] border-[#4EBC87]/25'
                              : n >= 7 ? 'bg-[#F59E0B]/15 text-[#92400E] border-[#F59E0B]/25'
                              : 'bg-[#D62B27]/15 text-[#D62B27] border-[#D62B27]/25';
                            return (
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full border ${tone}`}
                                data-testid={`resp-nps-${response.response_id}`}
                              >
                                NPS®: {Math.round(n)}
                              </span>
                            );
                          })()}
                          {response.sentiment && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full border ${getSentimentBadge(response.sentiment)}`}
                              data-testid={`resp-sentiment-${response.response_id}`}
                            >
                              {response.sentiment === 'positive' ? '😊 ' : response.sentiment === 'negative' ? '😟 ' : '😐 '}
                              Sentiment: {response.sentiment.charAt(0).toUpperCase() + response.sentiment.slice(1)}
                            </span>
                          )}
                          <span className="text-xs text-[#64748B]">
                            {branches.find(b => b.branch_id === response.branch_id)?.name || 'Unknown Branch'}
                          </span>
                        </div>
                        
                        {response.nps_reason && (
                          <p className="text-sm text-[#0F172A] line-clamp-2 mb-2">
                            "{response.nps_reason}"
                          </p>
                        )}

                        {Array.isArray(response.gap_ids) && response.gap_ids.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`response-gaps-${response.response_id}`}>
                            {response.gap_ids.map(gid => (
                              <span
                                key={gid}
                                className="inline-flex items-center gap-1 text-[11px] bg-[#1A3E6F] text-white rounded-full px-2 py-0.5"
                              >
                                <Tag className="w-3 h-3" />
                                {gapById[gid]?.name || gid}
                              </span>
                            ))}
                          </div>
                        )}

                        {response.has_profanity && (
                          <div className="mb-2">
                            <span
                              className="inline-flex items-center gap-1 text-[11px] bg-[#FFFBEB] text-[#92400E] border border-[#FCD34D] rounded-full px-2 py-0.5"
                              data-testid={`profanity-badge-${response.response_id}`}
                              title="This response contains profanity that has been masked"
                            >
                              <AlertTriangle className="w-3 h-3" /> Profanity detected
                            </span>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs text-[#64748B]">
                          {response.ratings?.overall_experience > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="w-3 h-3 fill-[#F1D511] text-[#F1D511]" />
                              Overall: {response.ratings.overall_experience}/5
                            </span>
                          )}
                          {response.answers && Object.keys(response.answers).length > 0 && (
                            <span className="text-[#1A3E6F] font-medium">{Object.keys(response.answers).length} answer(s)</span>
                          )}
                          {hasVoice(response) && (
                            <span className="flex items-center gap-1 text-[#D62B27]">
                              <Volume2 className="w-3 h-3" />
                              Voice
                            </span>
                          )}
                          <span>{formatDate(response.created_at)}</span>
                        </div>
                        
                        {response.redressal?.consent && (
                          <span className="inline-flex items-center gap-1 text-xs text-[#D62B27] mt-2">
                            <Phone className="w-3 h-3" />
                            Callback requested
                          </span>
                        )}
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#64748B]"
                        data-testid={`view-response-${response.response_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="summary" className="space-y-4 mt-0">
            {stats && <QuestionAggregationsCard aggregations={stats.question_aggregations} surveyQualifiersById={stats.survey_qualifiers_by_id || {}} />}
            {(!stats || !Array.isArray(stats.question_aggregations) || stats.question_aggregations.length === 0) && (
              <Card className="border-[#E2E8F0] shadow-sm">
                <CardContent className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-[#B4B4B4] mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-[#0F172A] font-['Montserrat']">No question summaries yet</h3>
                  <p className="text-[#64748B] mt-1 text-sm">Aggregated answers will appear here once responses are collected.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={exportOpen} onOpenChange={setExportOpen}>
          <DialogContent className="sm:max-w-[440px]" data-testid="export-csv-modal">
            <DialogHeader>
              <DialogTitle className="font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
                <Download className="w-4 h-4 text-[#1A3E6F]" />
                Export responses as CSV
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="text-xs font-medium text-[#0F172A] mb-1 block">Survey</label>
                <Select value={exportSurveyId} onValueChange={setExportSurveyId}>
                  <SelectTrigger data-testid="export-survey-select"><SelectValue placeholder="Select a survey" /></SelectTrigger>
                  <SelectContent>
                    {exportSurveys.map(s => (
                      <SelectItem key={s.survey_id || s.id} value={s.survey_id || s.id}>
                        {s.name || 'Untitled survey'}{s.status ? ` · ${s.status}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-[#0F172A] mb-1 block">Branch</label>
                <Select value={exportBranch} onValueChange={setExportBranch}>
                  <SelectTrigger data-testid="export-branch-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0F172A] mb-1 block">From</label>
                  <input
                    type="date"
                    value={exportFrom}
                    onChange={(e) => setExportFrom(e.target.value)}
                    className="h-9 w-full rounded-md border border-[#E2E8F0] bg-white px-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-[#1A3E6F]"
                    data-testid="export-date-from"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0F172A] mb-1 block">To</label>
                  <input
                    type="date"
                    value={exportTo}
                    onChange={(e) => setExportTo(e.target.value)}
                    className="h-9 w-full rounded-md border border-[#E2E8F0] bg-white px-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-[#1A3E6F]"
                    data-testid="export-date-to"
                  />
                </div>
              </div>

              <p className="text-[11px] text-[#94A3B8]">
                Leave date range blank to export all time. You will only see branches you have access to.
              </p>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setExportOpen(false)}
                  disabled={exporting}
                  data-testid="export-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={runExport}
                  disabled={exporting || !exportSurveyId}
                  className="bg-[#1A3E6F] hover:bg-[#15325A] text-white"
                  data-testid="export-confirm-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? 'Exporting…' : 'Export'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
